import { createSelector } from 'reselect';
import { RootReducer } from 'store/reducers';
import {
  Check,
  CheckPayload,
  ReviewResult,
  ReviewResultList,
} from 'types/check';
import { ChecksBrandingAlert } from 'types/check-results';
import {
  COUNTABLE_STATS_CATEGORIES,
  DocumentReviewCategory,
  ReviewCategory,
  StatisticByStatus,
} from 'types/review-category';
import { nanoid } from 'nanoid';
import { isBrandedDocument } from '../assessment/selectors';
import { selectedCategory, selectedCheck } from '../assessmentPage/selectors';

export const assessmentSummary = (state: RootReducer) =>
  state.assessmentSummary.summary;

const documentReviews = (state: RootReducer) =>
  state.assessmentSummary.summary?.documentReviews;

export const documentAlerts = (state: RootReducer) =>
  state.assessmentSummary.summary?.documentAlerts;

export const shouldRenderSnackBar = createSelector(
  selectedCategory,
  documentReviews,
  isBrandedDocument,
  (selectedCategory, documentReviews, isBrandedDocument): boolean => {
    if (
      !documentReviews ||
      selectedCategory !== ReviewCategory.brandingReview
    ) {
      return false;
    }

    const reviewInfo = documentReviews[DocumentReviewCategory.brandingReview];
    if (isBrandedDocument) {
      return reviewInfo.result === ReviewResult.fail;
    } else {
      return reviewInfo.checks.some(
        check =>
          check.result === ReviewResult.pass ||
          check.characteristic === 'ssiPresence'
      );
    }
  }
);

export const filter_branding_check_based_on_branding_status = (
  isBrandedDocument: boolean
) => {
  return (check: CheckPayload): boolean => {
    // case1: branded, all fails except ssiPresence
    const case1 =
      isBrandedDocument &&
      check.result === ReviewResult.fail &&
      check?.characteristic !== 'ssiPresence';

    // case2: unbranded, all passes and ssiPresence
    const case2 =
      !isBrandedDocument &&
      (check?.characteristic === 'ssiPresence' ||
        check.result === ReviewResult.fail);

    // only passes
    const case3 = check.result === ReviewResult.pass;

    return case1 || case2 || case3;
  };
};

export const getBrandingReview = createSelector(
  shouldRenderSnackBar,
  documentReviews,
  isBrandedDocument,
  (
    shouldRenderSnackBar,
    documentReviews,
    isBrandedDocument
  ): ChecksBrandingAlert | undefined => {
    if (!shouldRenderSnackBar || !documentReviews) return undefined;

    let reviewResult = ReviewResult.pass;
    const checks: Check[] = documentReviews[
      DocumentReviewCategory.brandingReview
    ].checks
      .filter(filter_branding_check_based_on_branding_status(isBrandedDocument))
      .map((check: CheckPayload) => {
        if (check.result === ReviewResult.fail) {
          reviewResult = ReviewResult.fail;
        }
        return { ...check } as Check;
      });

    return {
      id: nanoid(),
      result: reviewResult,
      checks,
    };
  }
);

export const isBrandingSelected = createSelector(
  getBrandingReview,
  selectedCheck,
  (getBrandingReview, selectedCheck): boolean =>
    getBrandingReview?.id === selectedCheck
);

export const getTotalStatisticByStatus = createSelector(
  documentAlerts,
  (documentAlerts): StatisticByStatus => {
    const defaultState = {
      [ReviewResult.fail]: 0,
      [ReviewResult.pass]: 0,
      [ReviewResult.warning]: 0,
    } as StatisticByStatus;

    if (!documentAlerts) return defaultState;

    return COUNTABLE_STATS_CATEGORIES.reduce((result, category) => {
      const documentCategory = documentAlerts[category];

      if (documentCategory) {
        result[ReviewResult.fail] += documentCategory[ReviewResult.fail];
        result[ReviewResult.pass] += documentCategory[ReviewResult.pass];
        result[ReviewResult.warning] += documentCategory[ReviewResult.warning];
      }

      return result;
    }, defaultState);
  }
);

export const getTotalCount = createSelector(
  getTotalStatisticByStatus,
  (statisticByStatus): number =>
    Object.values(statisticByStatus).reduce((result, category) => {
      return result + category;
    }, 0 as number)
);

export const getPercentage = (count: number, all: number): string => {
  if (!all) return '0%';
  return ((count / all) * 100).toFixed() + '%';
};

export const getDocumentPassPercentage = createSelector(
  getTotalStatisticByStatus,
  getTotalCount,
  (statisticByStatus, totalCount): string =>
    getPercentage(statisticByStatus[ReviewResult.pass], totalCount)
);

export const getAlertCountByStatus = createSelector(
  documentAlerts,
  (
    _: any,
    { category, result }: { category: ReviewCategory; result: ReviewResult }
  ) => ({
    category,
    result,
  }),
  (documentAlerts, { category, result }): number => {
    if (!documentAlerts) return 0;
    if (result === ReviewResult.skip) return 0;
    if (category === ReviewCategory.brandingReview) return 0;

    return documentAlerts[category][result];
  }
);

export const getAlertCountByCategory = (
  state: RootReducer,
  category: ReviewCategory
): number =>
  ReviewResultList.reduce(
    (acc, result) =>
      acc +
      getAlertCountByStatus(state, {
        category,
        result,
      }),
    0
  );
